/**
 * Safe Logger Utility
 * Prevents accidental logging of sensitive data (passwords, tokens, PII)
 *
 * Features:
 * - Automatic sanitization of sensitive fields
 * - Truncation of long strings
 * - Support for multiple log levels
 * - Development-only debug logging
 */

/**
 * List of sensitive field names that should be redacted
 */
const SENSITIVE_FIELDS = new Set([
  'password',
  'pwd',
  'token',
  'accesstoken',
  'apikey',
  'api_key',
  'secret',
  'clientsecret',
  'refreshtoken',
  'credential',
  'credentials',
  'auth',
  'authorization',
  'ssn',
  'socialsecuritynumber',
  'creditcard',
  'cardnumber',
  'cvv',
  'cvc',
  'pin',
  'email',
  'emailaddress',
  'phone',
  'phonenumber',
  'cellphone',
  'mobilenumber',
  'birthdate',
  'dateofbirth',
  'dob',
  'tin',
  'ein',
  'taxid',
  'passport',
  'licensekey',
  'privatekey',
  'jwt',
  'bearer',
  'session',
  'sessionid',
  'cookie',
  'authorization_code',
]);

/**
 * Log levels for controlling verbosity
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

/**
 * Sanitization options
 */
export interface SanitizationOptions {
  /** Maximum string length before truncation (default: 100) */
  maxStringLength?: number;
  /** Additional field names to treat as sensitive */
  additionalSensitiveFields?: string[];
  /** Replace sensitive values with this string (default: '[REDACTED]') */
  redactionPlaceholder?: string;
  /** Enable partial redaction of long strings (e.g., keep first/last chars) */
  partialRedaction?: boolean;
}

/**
 * Safe Logger utility for secure console logging
 *
 * Usage:
 * ```typescript
 * SafeLogger.info('User data:', userData); // Automatically sanitizes
 * SafeLogger.error('API error:', apiError);
 * SafeLogger.debug('Debug info:', debugData); // Only in development
 * ```
 */
export class SafeLogger {
  private static currentLogLevel: LogLevel = LogLevel.INFO;
  private static options: Required<SanitizationOptions> = {
    maxStringLength: 100,
    additionalSensitiveFields: [],
    redactionPlaceholder: '[REDACTED]',
    partialRedaction: false,
  };

  /**
   * Configure logger behavior
   */
  static configure(options: SanitizationOptions): void {
    this.options = {
      maxStringLength: options.maxStringLength ?? 100,
      additionalSensitiveFields: options.additionalSensitiveFields ?? [],
      redactionPlaceholder: options.redactionPlaceholder ?? '[REDACTED]',
      partialRedaction: options.partialRedaction ?? false,
    };
  }

  /**
   * Set the current log level
   */
  static setLogLevel(level: LogLevel): void {
    this.currentLogLevel = level;
  }

  /**
   * Check if a field name is considered sensitive
   */
  private static isSensitiveField(fieldName: string): boolean {
    const lowerName = fieldName.toLowerCase();
    if (SENSITIVE_FIELDS.has(lowerName)) {
      return true;
    }
    return this.options.additionalSensitiveFields.some(
      (field) => lowerName.includes(field.toLowerCase()) || field.toLowerCase().includes(lowerName)
    );
  }

  /**
   * Sanitize a single value
   */
  private static sanitizeValue(value: any): any {
    // Null/undefined pass through
    if (value === null || value === undefined) {
      return value;
    }

    // Small strings pass through (safe)
    if (typeof value === 'string' && value.length < 50) {
      return value;
    }

    // Long strings get truncated
    if (typeof value === 'string') {
      if (value.length > this.options.maxStringLength) {
        const truncated = value.substring(0, this.options.maxStringLength);
        return truncated + '...[truncated]';
      }
      return value;
    }

    // Primitives (numbers, booleans) pass through
    if (typeof value === 'boolean' || typeof value === 'number') {
      return value;
    }

    // Arrays: sanitize elements
    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeValue(item));
    }

    // Objects: sanitize recursively
    if (typeof value === 'object') {
      return this.sanitizeObject(value);
    }

    // Functions, symbols, etc: convert to string
    return String(value);
  }

  /**
   * Recursively sanitize an object, redacting sensitive fields
   */
  private static sanitizeObject(obj: any, depth: number = 0): any {
    // Prevent infinite recursion on circular references
    if (depth > 10) {
      return '[Object - max depth reached]';
    }

    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    // Handle special object types
    if (obj instanceof Error) {
      return {
        name: obj.name,
        message: obj.message,
        // Don't include stack traces in logs
      };
    }

    if (obj instanceof Date) {
      return obj.toISOString();
    }

    if (obj instanceof RegExp) {
      return obj.toString();
    }

    // Process plain objects
    const sanitized: Record<string, any> = {};

    for (const [key, value] of Object.entries(obj)) {
      if (this.isSensitiveField(key)) {
        // Redact sensitive fields entirely
        sanitized[key] = this.options.redactionPlaceholder;
      } else if (value === null || value === undefined) {
        sanitized[key] = value;
      } else if (typeof value === 'string') {
        if (value.length < 50) {
          sanitized[key] = value;
        } else {
          sanitized[key] = value.substring(0, this.options.maxStringLength) + '...[truncated]';
        }
      } else if (typeof value === 'object') {
        sanitized[key] = this.sanitizeObject(value, depth + 1);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Log at ERROR level
   */
  static error(message: string, data?: any): void {
    if (this.currentLogLevel > LogLevel.ERROR) {
      return;
    }

    if (typeof console !== 'undefined' && console.error) {
      if (data) {
        console.error(message, this.sanitizeObject(data));
      } else {
        console.error(message);
      }
    }
  }

  /**
   * Log at WARN level
   */
  static warn(message: string, data?: any): void {
    if (this.currentLogLevel > LogLevel.WARN) {
      return;
    }

    if (typeof console !== 'undefined' && console.warn) {
      if (data) {
        console.warn(message, this.sanitizeObject(data));
      } else {
        console.warn(message);
      }
    }
  }

  /**
   * Log at INFO level
   */
  static info(message: string, data?: any): void {
    if (this.currentLogLevel > LogLevel.INFO) {
      return;
    }

    if (typeof console !== 'undefined' && console.info) {
      if (data) {
        console.info(message, this.sanitizeObject(data));
      } else {
        console.info(message);
      }
    }
  }

  /**
   * Log at DEBUG level (only in development)
   */
  static debug(message: string, data?: any): void {
    if (this.currentLogLevel > LogLevel.DEBUG) {
      return;
    }

    // Only log debug in development mode
    if (typeof globalThis !== 'undefined' && (globalThis as any).process?.env?.NODE_ENV !== 'development') {
      return;
    }

    if (typeof console !== 'undefined' && console.debug) {
      if (data) {
        console.debug(message, this.sanitizeObject(data));
      } else {
        console.debug(message);
      }
    }
  }

  /**
   * Log an exception safely
   */
  static exception(message: string, error: Error): void {
    if (this.currentLogLevel > LogLevel.ERROR) {
      return;
    }

    if (typeof console !== 'undefined' && console.error) {
      const safeError = {
        name: error.name,
        message: error.message,
        // Only include sanitized stack info
        stack: error.stack?.substring(0, 200),
      };
      console.error(message, safeError);
    }
  }

  /**
   * Get a safe string representation of any value
   * Useful for logging without using console directly
   */
  static stringify(value: any, pretty: boolean = false): string {
    const sanitized = this.sanitizeValue(value);
    if (pretty) {
      return JSON.stringify(sanitized, null, 2);
    }
    return JSON.stringify(sanitized);
  }
}

/**
 * Create a child logger with a specific prefix
 */
export function createLogger(prefix: string): typeof SafeLogger {
  return {
    configure: (opts: any) => SafeLogger.configure(opts),
    setLogLevel: (level: any) => SafeLogger.setLogLevel(level),
    error: (msg: any, data?: any) => SafeLogger.error(`[${prefix}] ${msg}`, data),
    warn: (msg: any, data?: any) => SafeLogger.warn(`[${prefix}] ${msg}`, data),
    info: (msg: any, data?: any) => SafeLogger.info(`[${prefix}] ${msg}`, data),
    debug: (msg: any, data?: any) => SafeLogger.debug(`[${prefix}] ${msg}`, data),
    exception: (msg: any, err: any) => SafeLogger.exception(`[${prefix}] ${msg}`, err),
    stringify: (val: any, pretty?: any) => SafeLogger.stringify(val, pretty),
  } as any;
}
